/**
 * Storefront Error Capture Service
 *
 * Captures client-side errors (API failures, JS errors, network issues) and
 * buffers them for batch reporting to the admin API. Admins can view, filter,
 * and resolve errors from the Error Dashboard.
 *
 * Design:
 * - Errors are deduplicated by fingerprint (message + source) within a window
 * - Buffered in memory and flushed every 30 seconds or when buffer is full
 * - Falls back to localStorage if the API is unreachable
 * - No PII is captured (Senegal Loi 2008-12 compliance)
 */

import { ApiError, NetworkError, RateLimitError } from "./api-error-handler";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ErrorSeverity = "critical" | "high" | "medium" | "low";
export type ErrorCategory =
  | "api_error"
  | "network_error"
  | "rate_limit"
  | "js_error"
  | "render_error"
  | "payment_error"
  | "auth_error";

export interface CapturedError {
  readonly id: string;
  readonly fingerprint: string;
  readonly message: string;
  readonly category: ErrorCategory;
  readonly severity: ErrorSeverity;
  readonly source: string;
  readonly url: string;
  readonly user_agent: string;
  readonly http_status: number | null;
  readonly stack_trace: string | null;
  readonly metadata: Record<string, unknown>;
  readonly timestamp: string;
  readonly occurrence_count: number;
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const BUFFER_FLUSH_INTERVAL_MS = 30_000;
const MAX_BUFFER_SIZE = 50;
const DEDUP_WINDOW_MS = 60_000;
const LOCALSTORAGE_KEY = "djayma_error_buffer";
const API_URL = import.meta.env.VITE_API_URL ?? "";

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let buffer: CapturedError[] = [];
const recentFingerprints = new Map<string, { count: number; timestamp: number }>();
let flushTimer: ReturnType<typeof setInterval> | null = null;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateId(): string {
  return `err_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function fingerprint(message: string, source: string): string {
  return `${source}::${message}`.slice(0, 200);
}

function classifyError(error: unknown): {
  message: string;
  category: ErrorCategory;
  severity: ErrorSeverity;
  httpStatus: number | null;
  stackTrace: string | null;
  source: string;
} {
  if (error instanceof RateLimitError) {
    return {
      message: `Rate limited (retry after ${error.retryAfter}s)`,
      category: "rate_limit",
      severity: "low",
      httpStatus: 429,
      stackTrace: error.stack ?? null,
      source: "api",
    };
  }

  if (error instanceof ApiError) {
    const isPayment = error.message.toLowerCase().includes("payment") ||
      error.message.toLowerCase().includes("paiement");
    const isAuth = error.status === 401 || error.status === 403;

    let severity: ErrorSeverity = "medium";
    let category: ErrorCategory = "api_error";

    if (isPayment) {
      severity = "critical";
      category = "payment_error";
    } else if (isAuth) {
      severity = "high";
      category = "auth_error";
    } else if (error.status >= 500) {
      severity = "high";
    }

    return {
      message: error.message,
      category,
      severity,
      httpStatus: error.status,
      stackTrace: error.stack ?? null,
      source: "api",
    };
  }

  if (error instanceof NetworkError) {
    return {
      message: error.message,
      category: "network_error",
      severity: "medium",
      httpStatus: null,
      stackTrace: error.stack ?? null,
      source: "network",
    };
  }

  if (error instanceof Error) {
    return {
      message: error.message,
      category: "js_error",
      severity: "high",
      httpStatus: null,
      stackTrace: error.stack ?? null,
      source: "javascript",
    };
  }

  return {
    message: String(error),
    category: "js_error",
    severity: "medium",
    httpStatus: null,
    stackTrace: null,
    source: "unknown",
  };
}

// ---------------------------------------------------------------------------
// Core capture function
// ---------------------------------------------------------------------------

export function captureStorefrontError(
  error: unknown,
  extraMetadata?: Record<string, unknown>,
): void {
  try {
    const classified = classifyError(error);
    const fp = fingerprint(classified.message, classified.source);

    // Dedup within the window
    const existing = recentFingerprints.get(fp);
    if (existing && Date.now() - existing.timestamp < DEDUP_WINDOW_MS) {
      existing.count += 1;
      // Update the count on the buffered error
      const buffered = buffer.find((e) => e.fingerprint === fp);
      if (buffered) {
        // Immutable update — replace in buffer
        buffer = buffer.map((e) =>
          e.fingerprint === fp
            ? { ...e, occurrence_count: existing.count }
            : e,
        );
      }
      return;
    }

    recentFingerprints.set(fp, { count: 1, timestamp: Date.now() });

    // Clean up old fingerprints
    for (const [key, val] of recentFingerprints) {
      if (Date.now() - val.timestamp > DEDUP_WINDOW_MS * 2) {
        recentFingerprints.delete(key);
      }
    }

    const captured: CapturedError = {
      id: generateId(),
      fingerprint: fp,
      message: classified.message,
      category: classified.category,
      severity: classified.severity,
      source: classified.source,
      url: window.location.pathname,
      user_agent: navigator.userAgent,
      http_status: classified.httpStatus,
      stack_trace: classified.stackTrace,
      metadata: {
        ...extraMetadata,
        language: document.documentElement.lang || "fr",
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      },
      timestamp: new Date().toISOString(),
      occurrence_count: 1,
    };

    buffer.push(captured);

    // Flush immediately if buffer is full
    if (buffer.length >= MAX_BUFFER_SIZE) {
      flush();
    }
  } catch {
    // Error capture must never throw — silently discard
  }
}

// ---------------------------------------------------------------------------
// Flush — send buffered errors to the admin API
// ---------------------------------------------------------------------------

async function flush(): Promise<void> {
  if (buffer.length === 0) return;

  const batch = [...buffer];
  buffer = [];

  try {
    const res = await fetch(`${API_URL}/api/admin/errors/ingest`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ errors: batch }),
      // No credentials needed — ingest endpoint accepts anonymous reports
      // with a lightweight HMAC to prevent abuse
    });

    if (!res.ok) {
      // API unreachable — persist to localStorage for retry
      persistToLocalStorage(batch);
    }
  } catch {
    persistToLocalStorage(batch);
  }
}

function persistToLocalStorage(errors: readonly CapturedError[]): void {
  try {
    const existing = JSON.parse(
      localStorage.getItem(LOCALSTORAGE_KEY) ?? "[]",
    ) as CapturedError[];
    // Keep at most 200 errors in localStorage
    const merged = [...existing, ...errors].slice(-200);
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(merged));
  } catch {
    // localStorage full or unavailable — discard
  }
}

// ---------------------------------------------------------------------------
// Global error listeners
// ---------------------------------------------------------------------------

function handleWindowError(event: ErrorEvent): void {
  captureStorefrontError(event.error ?? new Error(event.message), {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
}

function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  captureStorefrontError(event.reason ?? new Error("Unhandled promise rejection"));
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

export function initErrorCapture(): void {
  // Start periodic flush
  if (flushTimer) clearInterval(flushTimer);
  flushTimer = setInterval(flush, BUFFER_FLUSH_INTERVAL_MS);

  // Listen for uncaught errors
  window.addEventListener("error", handleWindowError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);

  // Flush any errors persisted in localStorage from previous sessions
  try {
    const persisted = JSON.parse(
      localStorage.getItem(LOCALSTORAGE_KEY) ?? "[]",
    ) as CapturedError[];
    if (persisted.length > 0) {
      buffer.push(...persisted);
      localStorage.removeItem(LOCALSTORAGE_KEY);
    }
  } catch {
    // Ignore
  }

  // Flush on page unload
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flush();
    }
  });
}

export function destroyErrorCapture(): void {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  window.removeEventListener("error", handleWindowError);
  window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  flush();
}
